import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { CATEGORY_LABEL_KEY, LOCALE_LABEL_KEY } from './articleLabels';
import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  knowledgeApi,
  type ArticleCategory,
  type ArticleLocale,
  type KnowledgeArticle,
} from '../../lib/api/knowledge';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './ArticleFormDialog.module.css';

/**
 * The create/edit form for a knowledge-base article — `POST`/`PATCH
 * /knowledge-base(/{article})` (M-12, Task 4, spec K-2, K-3, K-4, K-6, K-8,
 * §2). Follows `DispensaryFormDialog.tsx` for the shape: one component,
 * `article` absent is create, present is edit.
 *
 * ## `answer` is a `<textarea>`, never `dangerouslySetInnerHTML` on the way
 * BACK OUT either (spec K-6, restated here for the write side)
 *
 * This dialog only ever WRITES `question`/`answer` as plain strings a
 * caller typed — no markdown, no HTML, nothing this form could construct
 * that the render site (`ArticleList.tsx`) would need to treat as anything
 * other than text. The stored-XSS finding spec §0 documents is answered by
 * the RENDER site refusing to interpret either field as HTML, not by this
 * form refusing to accept certain characters — accepting a stray `<script>`
 * here and rendering it as inert text there is the whole of K-6's decision.
 *
 * ## `is_published` ONLY APPEARS ON EDIT (spec K-2)
 *
 * `StoreArticleInput` carries no such field at all — a new entry is always
 * a draft, and `POST` never offers this dialog anywhere to say otherwise.
 * The checkbox appears only when `article` is present, and toggling it is
 * the ONE way this screen ever publishes or unpublishes an entry — there is
 * no separate publish action (spec §2's own "no publish endpoint of its
 * own").
 *
 * ## `category` is one of exactly four values (spec K-3)
 *
 * A `Select`, not free text, mirroring `JustificationFormDialog.tsx`'s
 * identical choice for its own closed `category` union — the legacy
 * `<select>` never emitted a fifth value either (spec K-3).
 */

export interface ArticleFormDialogProps {
  open: boolean;
  article?: KnowledgeArticle;
  onClose: () => void;
  onSaved: (article: KnowledgeArticle) => void;
}

const CATEGORIES: readonly ArticleCategory[] = ['sensor', 'replacement', 'defect', 'other'];
const LOCALES: readonly ArticleLocale[] = ['en', 'ar'];

interface FormState {
  question: string;
  answer: string;
  category: ArticleCategory;
  locale: ArticleLocale;
  isPublished: boolean;
}

function stateFor(article: KnowledgeArticle | undefined): FormState {
  if (article === undefined) {
    return { question: '', answer: '', category: 'sensor', locale: 'en', isPublished: false };
  }
  return {
    question: article.question,
    answer: article.answer,
    category: article.category,
    locale: article.locale,
    isPublished: article.is_published,
  };
}

/** No exported constant for this in `client.ts` yet — the same trap `DispensaryFormDialog.tsx` already documents. */
const FORBIDDEN_CODE = 'forbidden';

/** The only field slots this form actually renders an error next to — `category` and `locale` via `Select`'s own `error` prop, `question`/`answer` via the inline `<span className={styles['fieldError']}>`. A 422 keyed to anything else (e.g. `is_published`) must fall back to the general failure banner rather than being stored in `fieldErrors` and never shown (m-1). */
const RENDERED_FIELDS: ReadonlySet<string> = new Set(['question', 'answer', 'category', 'locale']);

export function ArticleFormDialog({ open, article, onClose, onSaved }: ArticleFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = article !== undefined;

  const [state, setState] = useState<FormState>(() => stateFor(article));
  const [missingFields, setMissingFields] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inFlight = useRef(false);
  const questionRef = useRef<HTMLTextAreaElement>(null);

  // Reused across opens — create, then an edit of a different row — rather
  // than remounted, so every field resets whenever it opens, keyed on the
  // row it is about to show. Mirrors `DispensaryFormDialog.tsx`.
  useEffect(() => {
    if (!open) return;
    setState(stateFor(article));
    setMissingFields({});
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, article]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('knowledge.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('knowledge.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('knowledge.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const missing: Record<string, boolean> = {};
    if (state.question.trim() === '') missing['question'] = true;
    if (state.answer.trim() === '') missing['answer'] = true;

    if (Object.keys(missing).length > 0) {
      setMissingFields(missing);
      return;
    }
    setMissingFields({});

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const saved =
        isEdit && article !== undefined
          ? await knowledgeApi.update(article.uuid, {
              question: state.question.trim(),
              answer: state.answer.trim(),
              category: state.category,
              locale: state.locale,
              is_published: state.isPublished,
            })
          : await knowledgeApi.create({
              question: state.question.trim(),
              answer: state.answer.trim(),
              category: state.category,
              locale: state.locale,
            });
      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      const mapped = mapDetails(error.details);
      const hasRenderedField = Object.keys(mapped).some((field) => RENDERED_FIELDS.has(field));
      if (hasRenderedField) {
        setFieldErrors(mapped);
      } else {
        // A 422 keyed only to a field this form has no slot for (e.g.
        // `is_published`) must not vanish into `fieldErrors` — fall back to
        // the general banner so the dialog never looks as if the save
        // silently did nothing (m-1).
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const categoryOptions: readonly SelectOption[] = CATEGORIES.map((value) => ({
    value,
    label: t(CATEGORY_LABEL_KEY[value]),
  }));
  const localeOptions: readonly SelectOption[] = LOCALES.map((value) => ({
    value,
    label: t(LOCALE_LABEL_KEY[value]),
  }));

  const dialogTitle = isEdit ? t('knowledge.editTitle') : t('knowledge.createTitle');

  return (
    <Dialog open={open} kind="form" size="md" onClose={onClose} initialFocusRef={questionRef} title={dialogTitle}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true" data-testid="articleFormFailure">
          {generalFailure}
        </div>

        <Textarea
          ref={questionRef}
          className={styles['wide']}
          label={t('knowledge.fieldQuestion')}
          dir="auto"
          value={state.question}
          error={missingFields['question'] ? t('knowledge.required') : fieldErrors['question']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, question: value }));
          }}
        />

        {/*
          The answer is a `<textarea>`, never a single-line input (spec
          K-6's closing note) — this file's own docblock.
        */}
        <Textarea
          className={styles['wide']}
          label={t('knowledge.fieldAnswer')}
          dir="auto"
          value={state.answer}
          error={missingFields['answer'] ? t('knowledge.required') : fieldErrors['answer']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, answer: value }));
          }}
        />

        <Select
          label={t('knowledge.fieldCategory')}
          width="fill"
          required
          options={categoryOptions}
          value={state.category}
          error={fieldErrors['category']}
          onChange={(event) => {
            const value = event.target.value as ArticleCategory;
            setState((current) => ({ ...current, category: value }));
          }}
        />

        <Select
          label={t('knowledge.fieldLocale')}
          width="fill"
          required
          options={localeOptions}
          value={state.locale}
          error={fieldErrors['locale']}
          onChange={(event) => {
            const value = event.target.value as ArticleLocale;
            setState((current) => ({ ...current, locale: value }));
          }}
        />

        {/*
          ONLY ON EDIT — see this file's own docblock. `POST` never offers
          this control at all (spec K-2): a new entry is always a draft.
        */}
        {isEdit ? (
          <label className={styles['publishRow']}>
            <input
              type="checkbox"
              checked={state.isPublished}
              onChange={(event) => {
                const { checked } = event.target;
                setState((current) => ({ ...current, isPublished: checked }));
              }}
            />
            {t('knowledge.fieldPublished')}
          </label>
        ) : null}

        <div className={styles['actions']}>
          <Button icon={<FloppyDiskIcon size={16} />} type="submit" loading={submitting}>
            {t('knowledge.save')}
          </Button>
          <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={onClose}>
            {t('knowledge.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
